import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';
import { Mail, Phone, MapPin, Users, FileText, Shield, ChevronRight, Menu, X, Building2, Briefcase, Home, Globe, Landmark, Award, TrendingUp, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  // Detailed content for all practice area cards
  const practiceDetails = {
    // Leagues
    'Bundesliga': {
      overview: 'The Bundesliga is Germany\'s top professional football league, renowned for its financial sustainability model, world-class stadiums, and passionate supporter culture. With 18 clubs competing each season, the Bundesliga is one of the most-watched football leagues globally, attracting top talent and generating significant commercial revenue.',
      keyServices: [
        'Player contract negotiations under German employment law',
        'Transfer advisory compliant with DFB and FIFA regulations',
        'Club licensing and Bundesliga financial fair play compliance',
        'Image rights structuring under German tax and IP law',
        'Work permit and immigration advisory for non-EU players'
      ],
      insights: [
        { title: 'The 50+1 Rule', summary: 'Germany\'s unique 50+1 ownership rule ensures that members retain majority voting rights in clubs. This creates distinctive legal considerations for investors and commercial partners seeking involvement in Bundesliga clubs.' },
        { title: 'Youth Development Regulations', summary: 'The Bundesliga\'s mandatory youth development programs and homegrown player requirements create specific contractual frameworks that differ from other European leagues.' },
        { title: 'Centralised Media Rights', summary: 'The DFL\'s centralized media rights distribution model affects how clubs negotiate broadcasting agreements and structure their commercial operations.' }
      ]
    },
    'Premier League': {
      overview: 'The Premier League is the world\'s most commercially successful football league, with 20 clubs competing in England\'s top division. Known for its global broadcasting reach, record-breaking transfer fees, and intense competition, the Premier League attracts the world\'s best players, managers, and investors.',
      keyServices: [
        'High-value transfer negotiations and Premier League contract structuring',
        'FA and Premier League regulatory compliance',
        'Profit and Sustainability Rules (PSR) advisory',
        'UK work permit and Governing Body Endorsement (GBE) applications',
        'Commercial and sponsorship deal negotiations'
      ],
      insights: [
        { title: 'Profit and Sustainability Rules', summary: 'The Premier League\'s financial regulations have become increasingly strict, with clubs facing points deductions for breaches. Our team advises on compliance strategies and financial structuring to meet PSR requirements.' },
        { title: 'Post-Brexit Player Registration', summary: 'The UK\'s departure from the EU has introduced the GBE points-based system for international player registration, creating new challenges for clubs recruiting from overseas.' },
        { title: 'Independent Football Regulator', summary: 'The introduction of an independent regulator for English football will bring significant changes to club governance, ownership, and financial oversight.' }
      ]
    },
    'Serie A': {
      overview: 'Serie A is Italy\'s premier football competition, home to some of the world\'s most historic and prestigious clubs. Known for its tactical sophistication and passionate culture, Serie A has undergone significant commercial growth in recent years with new ownership models and infrastructure investment.',
      keyServices: [
        'Player contracts under Italian labour and sports law',
        'FIGC regulatory compliance and licensing',
        'Italian tax optimization for international players (Decreto Crescita)',
        'Club acquisition and ownership structuring in Italy',
        'Stadium development and public-private partnerships'
      ],
      insights: [
        { title: 'Tax Benefits for International Players', summary: 'Italy\'s tax incentive regime for workers moving to Italy has been particularly attractive for football players, offering significant income tax reductions. Recent legislative changes require careful planning to maximize benefits.' },
        { title: 'Multi-Club Ownership in Serie A', summary: 'The growing trend of international investment groups acquiring Italian clubs raises complex questions under FIGC regulations and UEFA\'s multi-club ownership rules.' },
        { title: 'Stadium Modernization', summary: 'Italian clubs are increasingly pursuing new stadium projects, navigating complex municipal regulations and public-private partnership frameworks.' }
      ]
    },
    'Ligue 1': {
      overview: 'Ligue 1 is France\'s top professional football league, featuring 18 clubs competing at the highest level. The league has attracted significant international investment and elite talent, with French football producing many of the world\'s most sought-after players through its renowned academy system.',
      keyServices: [
        'Player contracts under French labour law and LFP regulations',
        'French tax planning for high-earning athletes',
        'FFF and LFP regulatory compliance',
        'Youth academy player development agreements',
        'Agent licensing and intermediary regulation in France'
      ],
      insights: [
        { title: 'French Social Charges and Tax Planning', summary: 'France\'s social security contributions and progressive tax rates require sophisticated structuring for player compensation packages to ensure competitiveness with other European leagues.' },
        { title: 'Academy Player Protection', summary: 'French law provides significant protections for academy players, including strict regulations on first professional contracts and training compensation mechanisms.' },
        { title: 'Ligue 1 Media Rights Evolution', summary: 'The restructuring of Ligue 1\'s broadcasting rights deals has significant implications for club revenues and financial planning.' }
      ]
    },
    'La Liga': {
      overview: 'La Liga is Spain\'s premier football division, globally recognized for its technical quality and home to some of the most iconic clubs in football history. The league has implemented progressive financial control measures and continues to expand its international reach through innovative commercial strategies.',
      keyServices: [
        'Player contracts under Spanish employment law',
        'La Liga financial fair play and salary cap compliance',
        'Spanish tax structuring for international athletes',
        'Release clause negotiations and enforcement',
        'Club licensing and RFEF regulatory matters'
      ],
      insights: [
        { title: 'La Liga Salary Cap System', summary: 'La Liga\'s unique squad cost limit system imposes strict financial controls on clubs. Understanding these regulations is essential for structuring compliant player contracts and transfer deals.' },
        { title: 'Release Clauses Under Spanish Law', summary: 'Spanish labour law mandates that all player contracts include a release clause. The strategic setting and enforcement of these clauses is a critical area of football legal practice.' },
        { title: 'Beckham Law Tax Regime', summary: 'Spain\'s special tax regime for incoming workers (Ley Beckham) can offer significant tax advantages for players relocating to Spain, subject to specific conditions and recent legislative changes.' }
      ]
    },
    'Eredivisie': {
      overview: 'The Eredivisie is the top professional football league in the Netherlands, celebrated worldwide for its commitment to attacking football and exceptional youth development. Dutch clubs have produced some of the game\'s greatest talents, and the league serves as an important stepping stone in international player careers.',
      keyServices: [
        'Player contracts under Dutch employment law',
        'KNVB regulatory compliance and licensing',
        'Youth player development and solidarity mechanism claims',
        'Dutch tax structuring for international athletes',
        'Transfer negotiations with sell-on and buy-back provisions'
      ],
      insights: [
        { title: 'Youth Development Model', summary: 'The Dutch football system\'s emphasis on youth academies creates specific legal frameworks for training compensation, solidarity contributions, and first professional contract negotiations.' },
        { title: 'Dutch Tax and Social Security', summary: 'The Netherlands offers specific tax arrangements for international employees (30% ruling) that can benefit incoming football players and coaching staff.' },
        { title: 'Sell-On Clause Structures', summary: 'Dutch clubs frequently negotiate complex sell-on provisions in transfer agreements. Proper structuring of these clauses is essential for maximizing future transfer revenue.' }
      ]
    },
    'Arab Leagues': {
      overview: 'Football leagues across the Middle East — including the Saudi Pro League, UAE Pro League, and Qatar Stars League — have experienced unprecedented growth and investment. With record-breaking transfers and ambitious sporting visions, the region has become a major destination for elite football talent and a dynamic market for sports business.',
      keyServices: [
        'High-value transfer negotiations to Middle Eastern clubs',
        'Player contracts compliant with local labour laws and federation rules',
        'Tax-free compensation structuring in Gulf states',
        'Sharia-compliant contractual arrangements where required',
        'Work visa and residency permit coordination'
      ],
      insights: [
        { title: 'Saudi Vision 2030 and Football', summary: 'Saudi Arabia\'s Vision 2030 has positioned football as a key pillar of economic diversification. The Saudi Pro League\'s significant investment in player recruitment has created a new global football market with unique legal and commercial opportunities.' },
        { title: 'Tax-Free Employment Structures', summary: 'Most Gulf states offer tax-free employment, but compensation structures must account for housing allowances, end-of-service benefits, and specific local labour law requirements.' },
        { title: 'FIFA Regulatory Considerations', summary: 'The rapid growth of Middle Eastern football has drawn increased FIFA regulatory scrutiny. Ensuring compliance with international transfer regulations and financial fair play standards is essential.' }
      ]
    },
    'MLS': {
      overview: 'Major League Soccer is the top professional football league in the United States and Canada. With a unique single-entity structure, salary cap system, and designated player rules, MLS presents a distinctive legal landscape. The league continues to expand rapidly, attracting international talent and growing its commercial footprint.',
      keyServices: [
        'MLS player contract structuring within salary cap framework',
        'Designated Player and allocation money negotiations',
        'US and Canadian work visa and immigration advisory (P-1 visas)',
        'MLS expansion team legal advisory',
        'Commercial and sponsorship agreements in the North American market'
      ],
      insights: [
        { title: 'Single-Entity Structure', summary: 'MLS\'s unique single-entity model means the league — not individual clubs — holds player contracts. This fundamentally different structure requires specialized legal knowledge for contract negotiations and dispute resolution.' },
        { title: 'Designated Player Rule', summary: 'The DP rule allows clubs to sign players outside the salary cap. Structuring these deals requires careful navigation of MLS roster rules, allocation money, and Targeted Allocation Money (TAM).' },
        { title: 'US Immigration for Athletes', summary: 'International players entering MLS require P-1 visas with specific evidentiary requirements. Our team coordinates the immigration process alongside contract negotiations to ensure timely player availability.' }
      ]
    },
    // Football Law
    'Professional Footballers': {
      overview: 'Our dedicated football law team provides comprehensive legal counsel to professional footballers at every stage of their careers. From youth academy contracts to top-flight transfers, we ensure our clients\' interests are protected across all major leagues worldwide.',
      keyServices: [
        'Contract negotiation and review for player agreements',
        'Image rights structuring and commercial endorsement deals',
        'Career management and transition planning',
        'Disciplinary proceedings and regulatory compliance',
        'Post-career business ventures and investment advisory'
      ],
      insights: [
        { title: 'FIFA Agent Regulations 2024', summary: 'The new FIFA Football Agent Regulations have transformed how players engage intermediaries. Our team guides clients through the updated licensing framework and fee cap structures.' },
        { title: 'Image Rights in the Digital Age', summary: 'With the rise of social media and NFTs, player image rights have become increasingly complex. We advise on multi-platform monetization while ensuring contractual compliance.' },
        { title: 'Cross-Border Employment Considerations', summary: 'Players moving between leagues face significant tax and employment law variations. Our international network ensures seamless transitions across jurisdictions.' }
      ]
    },
    'Football Clubs': {
      overview: 'We advise football clubs of all sizes on the legal and regulatory challenges of modern football governance. From transfer compliance to commercial structuring, our expertise spans the full spectrum of club operations.',
      keyServices: [
        'Transfer window strategy and FIFA compliance',
        'Club governance, ownership structuring, and FFP compliance',
        'Stadium development and naming rights agreements',
        'Broadcasting and media rights negotiations',
        'Youth academy contracts and development agreements'
      ],
      insights: [
        { title: 'Financial Fair Play Evolution', summary: 'UEFA\'s updated financial sustainability regulations impose new squad cost ratio requirements. We help clubs structure operations to maintain compliance while remaining competitive.' },
        { title: 'Multi-Club Ownership Structures', summary: 'The growing trend of multi-club ownership raises complex regulatory questions. Our team advises on structuring compliant ownership models across different football associations.' },
        { title: 'Commercial Revenue Maximization', summary: 'From matchday operations to global partnerships, we assist clubs in negotiating and structuring revenue-generating agreements.' }
      ]
    },
    'Agents & Intermediaries': {
      overview: 'The football intermediary landscape has undergone significant regulatory change. We provide comprehensive legal support to agents and intermediaries navigating the complex web of FIFA regulations, national association rules, and multi-party negotiations.',
      keyServices: [
        'FIFA agent licensing and compliance',
        'Representation agreement drafting and review',
        'Multi-party transfer negotiation support',
        'Fee dispute resolution and recovery',
        'Regulatory proceedings before FIFA and national FAs'
      ],
      insights: [
        { title: 'New FIFA Agent Exam Requirements', summary: 'The reintroduction of mandatory agent licensing requires thorough preparation. We support agents through the examination process and ongoing compliance obligations.' },
        { title: 'Fee Cap Implications', summary: 'FIFA\'s service fee caps have reshaped the intermediary business model. We advise on structuring compliant compensation arrangements.' },
        { title: 'Dispute Resolution Before FIFA', summary: 'Agent-related disputes before the FIFA Football Tribunal require specialized advocacy. Our team has extensive experience in FIFA proceedings.' }
      ]
    },
    'Family Offices': {
      overview: 'We assist professional footballers, high-net-worth families, and international entrepreneurs in establishing and managing sophisticated family office structures. Our multi-jurisdictional expertise ensures optimal wealth preservation, tax efficiency, and governance across borders.',
      keyServices: [
        'International family office structuring and establishment',
        'Multi-jurisdictional tax planning and wealth preservation',
        'Trust and estate planning for athletes and UHNW families',
        'Investment vehicle structuring and governance',
        'Succession planning and intergenerational wealth transfer'
      ],
      insights: [
        { title: 'Athletes and Wealth Management', summary: 'Professional athletes face unique financial planning challenges due to short career spans and international mobility. We design bespoke structures to protect and grow their wealth beyond their playing years.' },
        { title: 'Cross-Border Family Office Governance', summary: 'Operating a family office across multiple jurisdictions requires careful attention to regulatory compliance, reporting obligations, and governance frameworks.' },
        { title: 'Philanthropy and Impact Investing', summary: 'Many of our clients seek to create lasting social impact. We advise on establishing charitable foundations and structuring impact investment vehicles.' }
      ]
    },
    // Football Services
    'Player Transfers': {
      overview: 'Our transfer specialists handle domestic and international player movements across all major football leagues. We ensure compliance with FIFA regulations, negotiate favorable terms, and manage the complex documentation required for seamless transfers.',
      keyServices: [
        'Domestic and international transfer negotiations',
        'Loan deal structuring with buy-back and sell-on clauses',
        'FIFA Transfer Matching System (TMS) compliance',
        'Training compensation and solidarity mechanism calculations',
        'Emergency transfer and free agent arrangements'
      ],
      insights: [
        { title: 'Transfer Window Strategy', summary: 'Successful transfers require meticulous preparation months before the window opens. Our team advises on pre-agreement frameworks and conditional contract structures.' },
        { title: 'Solidarity Contributions', summary: 'FIFA\'s solidarity mechanism entitles training clubs to a percentage of transfer fees. We ensure all parties\' rights are properly calculated and enforced.' },
        { title: 'Third-Party Ownership Ban Compliance', summary: 'Following FIFA\'s ban on third-party ownership, creative financing structures require careful legal scrutiny to ensure full regulatory compliance.' }
      ]
    },
    'Contract Law': {
      overview: 'We draft, negotiate, and review the full range of contracts in professional football — from player employment agreements to multi-million-euro sponsorship deals. Our contracts team ensures clarity, enforceability, and protection of our clients\' interests.',
      keyServices: [
        'Player employment contract drafting and negotiation',
        'Sponsorship and commercial partnership agreements',
        'Coaching and management staff contracts',
        'Broadcasting and media appearance agreements',
        'Performance bonus and incentive structuring'
      ],
      insights: [
        { title: 'Contract Termination Without Just Cause', summary: 'FIFA Article 17 disputes remain among the most litigated issues in football. Our team has deep expertise in calculating compensation and managing regulatory consequences.' },
        { title: 'Morality Clauses in Modern Contracts', summary: 'Social media conduct and off-field behavior clauses have become standard. We advise on drafting enforceable provisions that protect both parties.' },
        { title: 'Deferred Compensation Structures', summary: 'Complex payment arrangements in player contracts require careful structuring to ensure tax efficiency and regulatory compliance across jurisdictions.' }
      ]
    },
    'Dispute Resolution': {
      overview: 'When disputes arise in professional football, swift and effective resolution is critical. Our litigation team represents clients before FIFA, the Court of Arbitration for Sport (CAS), and national football tribunals with a proven track record of success.',
      keyServices: [
        'FIFA Dispute Resolution Chamber proceedings',
        'Court of Arbitration for Sport (CAS) arbitration',
        'National football association tribunal representation',
        'Mediation and alternative dispute resolution',
        'Anti-doping proceedings and regulatory defense'
      ],
      insights: [
        { title: 'CAS Arbitration Trends', summary: 'Recent CAS decisions have shaped the landscape of football dispute resolution. We analyze emerging precedents to strengthen our clients\' positions.' },
        { title: 'Overdue Payables and Sporting Sanctions', summary: 'FIFA\'s overdue payables system can result in transfer bans. We assist clubs and players in resolving payment disputes before sanctions are imposed.' },
        { title: 'Contractual Stability Principles', summary: 'The balance between contractual stability and freedom of movement continues to evolve. Our team stays at the forefront of this developing area of sports law.' }
      ]
    },
    'International Family Offices': {
      overview: 'We provide end-to-end advisory services for establishing and managing international family offices, helping clients build structures that preserve wealth, ensure governance, and create lasting legacies across generations and jurisdictions.',
      keyServices: [
        'Family office formation in key jurisdictions (UK, Switzerland, Luxembourg, UAE)',
        'Investment governance frameworks and compliance',
        'Multi-generational wealth transfer and succession',
        'Real estate and alternative investment structuring',
        'Philanthropic foundation establishment'
      ],
      insights: [
        { title: 'Choosing the Right Jurisdiction', summary: 'The choice of family office domicile has significant implications for tax, regulation, and operational flexibility. We guide clients through comparative analysis of leading jurisdictions.' },
        { title: 'Digital Assets in Family Offices', summary: 'Cryptocurrency and digital asset management within family office structures requires specialized legal and compliance frameworks.' },
        { title: 'Family Governance Charters', summary: 'Clear governance documentation prevents future disputes and ensures smooth intergenerational transition of control and values.' }
      ]
    },
    // Entertainment & IP
    'Music & Events': {
      overview: 'Our entertainment law practice provides comprehensive legal services to artists, promoters, venues, and event organizers. We handle everything from recording contracts to international touring agreements, ensuring our clients\' creative and commercial interests are fully protected.',
      keyServices: [
        'Artist management and recording contracts',
        'Music licensing, publishing, and royalty agreements',
        'Live event and festival contracts',
        'International touring agreements and work permits',
        'Performance rights and collective management'
      ],
      insights: [
        { title: 'Streaming Revenue and Artist Rights', summary: 'The shift to digital streaming has fundamentally changed music industry economics. We advise artists on maximizing streaming revenue and protecting their rights in digital distribution agreements.' },
        { title: 'Live Event Liability and Insurance', summary: 'Post-pandemic event contracts require enhanced force majeure provisions and comprehensive insurance structuring.' },
        { title: 'AI-Generated Music and Copyright', summary: 'The emergence of AI in music creation raises novel copyright questions. We advise on protecting human creativity while navigating this evolving legal landscape.' }
      ]
    },
    'TV & Entertainment Rights': {
      overview: 'We advise broadcasters, production companies, streaming platforms, and content creators on the acquisition, licensing, and exploitation of entertainment rights across all media. Our expertise spans traditional broadcasting to emerging digital platforms.',
      keyServices: [
        'Broadcasting rights acquisition and licensing',
        'TV and film production agreements',
        'Content distribution and platform deals',
        'Format rights protection and licensing',
        'Talent agreements and residual compensation'
      ],
      insights: [
        { title: 'Sports Broadcasting Rights', summary: 'The competition for sports broadcasting rights continues to intensify. We advise rights holders and broadcasters on structuring deals that maximize value across traditional and digital platforms.' },
        { title: 'Content Licensing in the Streaming Era', summary: 'Multi-territory content licensing requires careful attention to holdback periods, exclusivity windows, and platform-specific rights.' },
        { title: 'Reality TV and Format Protection', summary: 'Television formats represent valuable intellectual property. We advise on protecting format rights internationally and pursuing infringement claims.' }
      ]
    },
    'Trademarks & Brands': {
      overview: 'Our trademark and brand protection practice helps businesses, athletes, and entertainers build, protect, and monetize their brands globally. From registration to enforcement, we provide strategic counsel on all aspects of trademark law.',
      keyServices: [
        'International trademark registration and portfolio management',
        'Brand protection and anti-counterfeiting strategies',
        'Trademark licensing and merchandising agreements',
        'Domain name disputes and online brand protection',
        'Trademark opposition and cancellation proceedings'
      ],
      insights: [
        { title: 'Athlete Personal Brands', summary: 'Professional athletes increasingly build personal brands that extend well beyond their sport. We advise on trademark registration strategies that protect names, nicknames, logos, and signature celebrations.' },
        { title: 'EU Trademark Reform', summary: 'Recent changes to EU trademark law have implications for filing strategies, examination procedures, and enforcement mechanisms across member states.' },
        { title: 'Metaverse and Virtual Brand Protection', summary: 'As brands enter virtual environments, trademark protection must extend to digital goods and services. We advise on securing rights in emerging virtual marketplaces.' }
      ]
    },
    'Patents & Models': {
      overview: 'We provide comprehensive patent and design protection services for inventors, technology companies, and creative businesses. Our team handles the full lifecycle from initial filing through prosecution to enforcement and commercialization.',
      keyServices: [
        'Patent application drafting and prosecution',
        'Utility model and design registration',
        'Patent portfolio strategy and management',
        'Freedom-to-operate analysis and due diligence',
        'Patent licensing and technology transfer agreements'
      ],
      insights: [
        { title: 'Sports Technology Patents', summary: 'Innovation in sports technology — from wearable performance trackers to VAR systems — generates significant patentable IP. We advise technology companies operating in the sports sector.' },
        { title: 'Standard Essential Patents', summary: 'SEP licensing in telecommunications and IoT technologies requires careful navigation of FRAND obligations and competition law considerations.' },
        { title: 'Design Protection Across Jurisdictions', summary: 'Protecting product designs internationally requires strategic filing across multiple design registration systems with varying requirements.' }
      ]
    },
    'Copyright & IP Litigation': {
      overview: 'When intellectual property rights are infringed, decisive legal action is essential. Our IP litigation team has extensive experience in copyright enforcement, infringement proceedings, and royalty recovery across multiple jurisdictions.',
      keyServices: [
        'Copyright infringement proceedings and enforcement',
        'Royalty recovery and audit rights',
        'Cross-border IP litigation coordination',
        'Injunctive relief and emergency proceedings',
        'Alternative dispute resolution for IP matters'
      ],
      insights: [
        { title: 'Digital Copyright Enforcement', summary: 'Online piracy and unauthorized use of copyrighted content require sophisticated enforcement strategies combining legal action with platform-specific takedown mechanisms.' },
        { title: 'Music Copyright Sampling Disputes', summary: 'The line between inspiration and infringement in music sampling continues to be tested. We represent both rights holders and creators in these complex disputes.' },
        { title: 'International IP Enforcement', summary: 'Coordinating IP enforcement across multiple jurisdictions requires strategic planning and local expertise. Our international network enables effective cross-border action.' }
      ]
    },
    'IP Portfolio Management': {
      overview: 'We help businesses and individuals build, manage, and monetize comprehensive intellectual property portfolios. Our strategic approach ensures maximum value extraction from IP assets while maintaining robust protection.',
      keyServices: [
        'IP audit and asset valuation',
        'Portfolio strategy development and optimization',
        'Licensing program design and management',
        'IP due diligence for M&A transactions',
        'International IP filing and maintenance coordination'
      ],
      insights: [
        { title: 'IP Valuation in Sports Transactions', summary: 'Club acquisitions and sports business transactions increasingly hinge on IP valuation — from broadcasting rights to brand equity and digital assets.' },
        { title: 'Open Innovation and IP Strategy', summary: 'Collaborative innovation models require carefully structured IP arrangements that balance openness with protection of core assets.' },
        { title: 'IP and ESG Reporting', summary: 'Growing ESG disclosure requirements are beginning to encompass intangible assets. We advise on IP-related ESG reporting and sustainable innovation strategies.' }
      ]
    },
    // Business Law
    'Corporate & Commercial': {
      overview: 'Our corporate and commercial practice provides comprehensive legal counsel to businesses operating domestically and internationally. We advise on all aspects of corporate life, from formation and governance through to complex M&A transactions.',
      keyServices: [
        'Corporate banking and finance transactions',
        'Mergers, acquisitions, and due diligence',
        'Joint ventures and strategic partnerships',
        'Corporate governance and compliance frameworks',
        'EU and competition law advisory'
      ],
      insights: [
        { title: 'Cross-Border M&A Trends', summary: 'International mergers and acquisitions require careful navigation of multiple regulatory regimes. Our team provides seamless cross-border transaction support through our global network.' },
        { title: 'ESG and Corporate Governance', summary: 'Environmental, Social, and Governance considerations are increasingly central to corporate decision-making. We advise boards on integrating ESG into governance frameworks.' },
        { title: 'Competition Law Compliance', summary: 'With increased enforcement activity across jurisdictions, robust competition law compliance programs are essential for businesses operating across borders.' }
      ]
    },
    'Tax & Accounting': {
      overview: 'Our tax and accounting team provides strategic advisory services to businesses and high-net-worth individuals. We combine deep technical expertise with practical commercial understanding to deliver optimal tax-efficient solutions.',
      keyServices: [
        'Corporate tax planning and structuring',
        'International tax advisory and transfer pricing',
        'Business accounting and financial reporting',
        'Customs duties and indirect tax advisory',
        'Tax dispute resolution and authority negotiations'
      ],
      insights: [
        { title: 'Global Minimum Tax Implementation', summary: 'The OECD Pillar Two global minimum tax is reshaping international tax planning. We advise multinational groups on compliance and strategic restructuring.' },
        { title: 'Athletes and International Tax Planning', summary: 'International athletes face complex tax obligations across multiple jurisdictions. We provide bespoke tax planning that ensures compliance while optimizing their global tax position.' },
        { title: 'Transfer Pricing in the Digital Economy', summary: 'Digital business models challenge traditional transfer pricing approaches. We advise on compliant pricing structures for digital goods and services.' }
      ]
    },
    'Real Estate': {
      overview: 'We advise on all aspects of real estate law, from residential acquisitions to major commercial developments. Our practice covers the complete property lifecycle including planning, construction, financing, and dispute resolution.',
      keyServices: [
        'Property acquisition and disposal',
        'Real estate development and construction contracts',
        'Commercial leasing and tenant representation',
        'Real estate finance and investment structuring',
        'Planning, zoning, and regulatory compliance'
      ],
      insights: [
        { title: 'Sports Facility Development', summary: 'Stadium and training facility projects involve complex planning, construction, and financing arrangements. We advise clubs and developers on structuring these landmark projects.' },
        { title: 'International Property Investment', summary: 'Cross-border real estate investment requires careful attention to local regulations, tax implications, and ownership structuring.' },
        { title: 'Sustainable Building Regulations', summary: 'Evolving environmental regulations are reshaping construction and development requirements. We advise on compliance with green building standards.' }
      ]
    },
    'Intellectual Property': {
      overview: 'Our intellectual property practice provides comprehensive protection for creative and commercial IP assets. We handle the full range of IP matters from registration through enforcement and commercialization.',
      keyServices: [
        'Copyright, trademark, and patent registration',
        'IP licensing and technology transfer',
        'Media, TV, and film rights',
        'IP infringement litigation and enforcement',
        'Design protection and industrial property'
      ],
      insights: [
        { title: 'AI and IP Ownership', summary: 'The question of who owns AI-generated content remains one of the most pressing issues in IP law. We advise clients navigating this rapidly evolving area.' },
        { title: 'Brand Protection in E-commerce', summary: 'Online marketplaces present unique challenges for brand owners. We develop comprehensive strategies combining platform tools with legal enforcement.' },
        { title: 'IP in Sports Merchandising', summary: 'Sports merchandising generates significant revenue but requires robust IP protection. We advise on licensing programs and anti-counterfeiting strategies.' }
      ]
    },
    'Sports & Tourism': {
      overview: 'Our sports and tourism practice serves clubs, federations, athletes, tourism operators, and hospitality businesses. We combine deep sports law expertise with commercial acumen to deliver practical solutions.',
      keyServices: [
        'Player representation and transfer advisory',
        'Club governance and regulatory compliance',
        'Sports event management and hosting agreements',
        'Tourism business structuring and licensing',
        'Hospitality and leisure industry advisory'
      ],
      insights: [
        { title: 'Sports Event Hosting Rights', summary: 'Bidding for and hosting major sports events involves complex legal, commercial, and regulatory considerations. We advise host cities and organizing committees.' },
        { title: 'Athlete Endorsement Agreements', summary: 'Structuring endorsement deals requires careful attention to exclusivity, territory, usage rights, and morality provisions.' },
        { title: 'Tourism Regulation Post-Pandemic', summary: 'Evolving travel regulations and consumer protection requirements demand updated legal frameworks for tourism operators.' }
      ]
    },
    'Family & Estate': {
      overview: 'Our family and estate practice provides sensitive, expert guidance on personal and family legal matters. We help high-net-worth families, athletes, and business owners protect their legacies and plan for the future.',
      keyServices: [
        'Family law and matrimonial proceedings',
        'Succession planning and will drafting',
        'Trust establishment and administration',
        'Estate management and wealth protection',
        'Prenuptial and postnuptial agreements'
      ],
      insights: [
        { title: 'Athlete Wealth Protection', summary: 'Professional athletes need robust estate planning from early in their careers. We design structures that protect wealth through career transitions and beyond.' },
        { title: 'International Succession Planning', summary: 'Families with assets in multiple countries face complex succession issues. We coordinate estate planning across jurisdictions to avoid conflicts of law.' },
        { title: 'Family Business Succession', summary: 'Transitioning a family business to the next generation requires careful legal and tax planning to preserve both the business and family relationships.' }
      ]
    },
    'International Business': {
      overview: 'We advise businesses on the legal complexities of international trade and cross-border operations. Our global network enables us to provide seamless multi-jurisdictional support for businesses expanding internationally.',
      keyServices: [
        'Cross-border transactions and trade agreements',
        'International trade compliance and sanctions',
        'Telecommunications and technology regulation',
        'Energy, utilities, and environmental law',
        'Foreign direct investment and market entry strategies'
      ],
      insights: [
        { title: 'Sanctions and Trade Compliance', summary: 'Rapidly evolving international sanctions regimes require constant vigilance. We help businesses maintain compliance while pursuing international opportunities.' },
        { title: 'International Arbitration', summary: 'Cross-border commercial disputes increasingly turn to international arbitration. We represent clients in proceedings under ICC, LCIA, and other major institutional rules.' },
        { title: 'Market Entry Strategy', summary: 'Entering new markets requires careful assessment of regulatory requirements, local partnerships, and risk management frameworks.' }
      ]
    },
    'Technology & Media': {
      overview: 'We advise technology companies, media businesses, and digital platforms on the legal challenges of innovation and digital transformation. Our practice spans data protection, e-commerce, gaming, and emerging technologies.',
      keyServices: [
        'E-commerce platform and marketplace regulation',
        'Data protection and privacy compliance (GDPR)',
        'Gaming and interactive entertainment law',
        'Digital advertising and marketing regulation',
        'Fintech and blockchain regulatory advisory'
      ],
      insights: [
        { title: 'AI Regulation in the EU', summary: 'The EU AI Act creates new compliance obligations for businesses deploying AI systems. We advise on classification, risk assessment, and compliance strategies.' },
        { title: 'Data Transfer Post-Privacy Shield', summary: 'International data transfers require careful structuring following regulatory developments. We advise on compliant transfer mechanisms and data processing agreements.' },
        { title: 'Digital Markets Act Compliance', summary: 'The DMA imposes significant obligations on digital gatekeepers. We advise platforms and businesses on compliance and enforcement implications.' }
      ]
    },
    'Litigation & Disputes': {
      overview: 'Our dispute resolution team handles complex commercial litigation, arbitration, and mediation across multiple jurisdictions. We combine aggressive advocacy with strategic thinking to achieve optimal outcomes for our clients.',
      keyServices: [
        'Civil and commercial litigation',
        'International arbitration (ICC, LCIA, CAS)',
        'Employment and labor disputes',
        'Bankruptcy and insolvency proceedings',
        'Mediation and alternative dispute resolution'
      ],
      insights: [
        { title: 'Third-Party Litigation Funding', summary: 'Litigation funding is transforming access to justice for commercial claims. We advise on structuring funded proceedings and managing funder relationships.' },
        { title: 'Cross-Border Enforcement', summary: 'Obtaining a judgment is only half the battle. We advise on enforcement strategies across jurisdictions, including asset tracing and recovery.' },
        { title: 'Class Actions in Europe', summary: 'The EU Representative Actions Directive is creating new collective redress mechanisms. We advise businesses on managing exposure and defending group claims.' }
      ]
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0008] relative">
      {/* Global bordeaux tint overlay */}
      <div className="fixed inset-0 pointer-events-none z-[1] bg-gradient-to-b from-[#800020]/5 via-[#800020]/8 to-[#800020]/12 mix-blend-overlay"></div>
      {/* FULL WIDTH WHITE HEADER BANNER */}
      <header className="fixed w-full z-50 bg-white shadow-2xl" data-testid="main-navigation">
        <div className="w-full flex items-center justify-between h-36 md:h-44 px-6 md:px-12 lg:px-16 border-b-4 border-[#800020]">
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
            <img 
              src="/accomply-logo.png" 
              alt="Accomply Global" 
              className="h-24 md:h-32 w-auto hover:scale-105 transition-transform"
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-black hover:text-[#800020] transition-colors font-bold text-sm uppercase tracking-wider">Home</button>
            <button onClick={() => scrollToSection('football')} className="text-black hover:text-[#800020] transition-colors font-bold text-sm uppercase tracking-wider">Football Law</button>
            <button onClick={() => scrollToSection('entertainment')} className="text-black hover:text-[#800020] transition-colors font-bold text-sm uppercase tracking-wider">Entertainment & IP</button>
            <button onClick={() => scrollToSection('services')} className="text-black hover:text-[#800020] transition-colors font-bold text-sm uppercase tracking-wider">Business Law</button>
            <button onClick={() => scrollToSection('team')} className="text-black hover:text-[#800020] transition-colors font-bold text-sm uppercase tracking-wider">Team</button>
            <button onClick={() => scrollToSection('contact')} className="bg-[#800020] hover:bg-[#A91D3A] text-white px-6 py-3 font-bold uppercase tracking-widest transition-all hover:scale-105 shadow-lg" data-testid="contact-cta">Contact Us</button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="lg:hidden text-black"
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200" data-testid="mobile-menu">
            <div className="px-6 py-6 space-y-4">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Home</button>
              <button onClick={() => scrollToSection('football')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Football Law</button>
              <button onClick={() => scrollToSection('entertainment')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Entertainment & IP</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Business Law</button>
              <button onClick={() => scrollToSection('team')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Team</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-black hover:text-[#800020] font-bold text-sm uppercase tracking-wider py-2">Contact</button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        id="home" 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1556816213-00d1ffaa2f78?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0008]/80 via-[#0a0008]/90 to-[#0a0008]"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-52">
          <div className="animate-fade-in">
            <div className="inline-block mb-8">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-8 py-3 text-sm font-black uppercase tracking-[0.3em]">Elite Legal Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 leading-[0.9] tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>
              Global Legal Excellence in
              <span className="block bg-gradient-to-r from-[#800020] via-[#C73659] to-[#D4AF37] bg-clip-text text-transparent mt-6">Football, Intellectual Property & Business Law</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              Representing top-tier clubs, elite footballers, artists, and international businesses — from sports and entertainment to intellectual property and family offices worldwide
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => scrollToSection('contact')} 
                className="bg-[#800020] hover:bg-[#A91D3A] text-white px-12 py-5 font-black uppercase tracking-[0.2em] text-lg hover:scale-105 transition-all shadow-[0_0_30px_rgba(128,0,32,0.5)]"
                data-testid="get-started-button"
              >
                Get Started
              </button>
              <button 
                onClick={() => scrollToSection('football')} 
                className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-12 py-5 font-black uppercase tracking-[0.2em] text-lg hover:scale-105 transition-all"
                data-testid="our-services-button"
              >
                Our Expertise
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-8 mt-24 max-w-5xl mx-auto">
            {[
              { icon: Award, label: '25+ Years', sublabel: 'Experience' },
              { icon: Globe, label: '8 Leagues', sublabel: 'Worldwide' },
              { icon: Star, label: 'Top Tier', sublabel: 'Clients' }
            ].map((stat, idx) => (
              <div key={idx} className="bg-[#110a14] border border-[#800020]/30 p-8 hover:border-[#D4AF37] hover:shadow-[0_0_30px_rgba(212,175,55,0.2)] transition-all hover:-translate-y-2">
                <stat.icon className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-3xl font-black text-white">{stat.label}</p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Football & Sports Law Section */}
      <section 
        id="football"
        className="relative py-32 px-6 md:px-12 overflow-hidden bg-[#0a0008]" 
        data-testid="football-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.3em]">⚽ Our Specialization</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>Football & Sports Law Excellence</h2>
            <p className="text-2xl text-gray-300 font-light">Representing Elite Clubs, Agents & Footballers Across Major Leagues</p>
          </div>

          {/* Bento Grid for Leagues */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {[
              { flag: '🇩🇪', league: 'Bundesliga', country: 'Germany' },
              { flag: '🇬🇧', league: 'Premier League', country: 'UK' },
              { flag: '🇮🇹', league: 'Serie A', country: 'Italy' },
              { flag: '🇫🇷', league: 'Ligue 1', country: 'France' },
              { flag: '🇪🇸', league: 'La Liga', country: 'Spain' },
              { flag: '🇳🇱', league: 'Eredivisie', country: 'Holland' },
              { flag: '🌍', league: 'Arab Leagues', country: 'Middle East' },
              { flag: '🇺🇸', league: 'MLS', country: 'USA' }
            ].map((item, idx) => (
              <div key={idx} className="group relative bg-[#110a14] border border-[#800020]/20 hover:border-[#D4AF37] flex flex-col items-center justify-center py-6 px-4 transition-all hover:-translate-y-2 cursor-pointer" onClick={() => setActiveModal(item.league)} data-testid={`league-card-${idx}`}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#800020]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="text-3xl mb-2 relative z-10">{item.flag}</div>
                <p className="text-white font-bold text-sm relative z-10">{item.league}</p>
                <p className="text-gray-400 text-xs relative z-10">{item.country}</p>
                <p className="text-[#D4AF37] text-xs font-bold mt-2 uppercase tracking-wider relative z-10">Learn More →</p>
              </div>
            ))}
          </div>

          {/* Client Types */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { icon: '⚽', title: 'Professional Footballers', desc: 'Contract negotiations, career management, image rights, endorsements' },
              { icon: '🏟️', title: 'Football Clubs', desc: 'Regulatory compliance, governance, transfer strategy, commercial deals' },
              { icon: '🤝', title: 'Agents & Intermediaries', desc: 'License compliance, representation agreements, multi-party negotiations' },
              { icon: '🏛️', title: 'Family Offices', desc: 'Assisting players\' families and high-net-worth families to structure and manage international family offices' }
            ].map((client, idx) => (
              <div key={idx} className="bg-[#110a14] border border-[#800020]/30 p-8 hover:border-[#D4AF37] transition-all cursor-pointer hover:-translate-y-2" onClick={() => setActiveModal(client.title)} data-testid={`client-card-${idx}`}>
                <div className="text-4xl mb-4">{client.icon}</div>
                <h4 className="text-2xl font-bold text-white mb-3">{client.title}</h4>
                <p className="text-gray-400 leading-relaxed">{client.desc}</p>
                <p className="text-[#D4AF37] text-sm font-bold mt-4 uppercase tracking-wider">Learn More →</p>
              </div>
            ))}
          </div>

          {/* Football Services */}
          <div className="bg-gradient-to-r from-[#800020] to-[#A91D3A] p-12 border-t-4 border-[#D4AF37]">
            <h3 className="text-3xl font-black text-white mb-10 text-center uppercase tracking-wider">Football Law Services</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: TrendingUp, title: 'Player Transfers', desc: 'Domestic & international transfers, loan deals, FIFA regulations' },
                { icon: FileText, title: 'Contract Law', desc: 'Player contracts, sponsorships, commercial partnerships' },
                { icon: Shield, title: 'Dispute Resolution', desc: 'FIFA disputes, CAS arbitration, contractual disagreements' },
                { icon: Landmark, title: 'International Family Offices', desc: 'Wealth structuring, multi-jurisdiction planning, and family office setup for players and wealthy families' }
              ].map((service, idx) => (
                <div key={idx} className="bg-black/20 backdrop-blur-sm p-8 text-center hover:bg-black/40 transition-all cursor-pointer hover:-translate-y-1" onClick={() => setActiveModal(service.title)} data-testid={`football-service-card-${idx}`}>
                  <service.icon className="h-14 w-14 text-[#D4AF37] mx-auto mb-4" />
                  <h4 className="font-black text-xl mb-3 text-white uppercase tracking-wide">{service.title}</h4>
                  <p className="text-gray-200">{service.desc}</p>
                  <p className="text-[#D4AF37] text-sm font-bold mt-4 uppercase tracking-wider">Learn More →</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Entertainment & IP Section */}
      <section id="entertainment" className="relative py-32 px-6 md:px-12 overflow-hidden bg-[#0d0510]" data-testid="entertainment-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.3em]">Entertainment & IP</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>Intellectual Property & Entertainment</h2>
            <p className="text-2xl text-gray-300 font-light">Protecting Creative Assets & Commercial Rights Worldwide</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Globe, title: 'Music & Events', desc: 'Artist management, music licensing, live event contracts, touring agreements, and performance rights' },
              { icon: FileText, title: 'TV & Entertainment Rights', desc: 'Broadcasting rights, TV production deals, content licensing, distribution agreements, and media contracts' },
              { icon: Shield, title: 'Trademarks & Brands', desc: 'Trademark registration, brand protection, licensing, enforcement, and anti-counterfeiting strategies' },
              { icon: Award, title: 'Patents & Models', desc: 'Patent applications, utility models, design protection, technical IP, and invention commercialization' },
              { icon: Landmark, title: 'Copyright & IP Litigation', desc: 'Copyright enforcement, infringement disputes, royalty recovery, and cross-border IP litigation' },
              { icon: Briefcase, title: 'IP Portfolio Management', desc: 'Strategic IP audits, portfolio structuring, licensing programs, and international IP asset management' }
            ].map((service, idx) => (
              <div key={idx} className="group bg-[#110a14] border border-[#800020]/20 hover:border-[#D4AF37] p-8 hover:-translate-y-2 transition-all cursor-pointer" onClick={() => setActiveModal(service.title)} data-testid={`entertainment-card-${idx}`}>
                <service.icon className="h-14 w-14 text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.desc}</p>
                <p className="text-[#D4AF37] text-sm font-bold mt-4 uppercase tracking-wider">Learn More →</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 md:px-12 bg-[#0d0510]" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.3em]">Full Service</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>Business Law</h2>
            <p className="text-xl text-gray-400">Comprehensive solutions for businesses and private clients worldwide</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Corporate & Commercial', description: 'Corporate banking, finance, compliance, governance, EU & competition law, joint ventures, M&A, due diligence', icon: Building2 },
              { title: 'Tax & Accounting', description: 'Business accounting, tax advisory services, customs duties, corporate structuring, financial planning', icon: Briefcase },
              { title: 'Real Estate', description: 'Property rental, acquisition, planning, construction, structuring, and real estate litigation', icon: Home },
              { title: 'Intellectual Property', description: 'Copyright, trademarks, patents, design protection, media, TV, movies, and IP infringement cases', icon: Shield },
              { title: 'Sports & Tourism', description: 'Player representation, club advisory, transfer negotiations, contract law, FIFA dispute resolution', icon: Users },
              { title: 'Family & Estate', description: 'Family law, succession planning, trust & estate management, wealth protection', icon: Users },
              { title: 'International Business', description: 'Cross-border transactions, international trade, telecommunications, energy utilities, environmental law', icon: Globe },
              { title: 'Technology & Media', description: 'E-commerce, new technologies, privacy data protection, entertainment, gaming, advertising', icon: FileText },
              { title: 'Litigation & Disputes', description: 'Civil, commercial, labor disputes, arbitration, mediation, bankruptcy, alternative dispute resolution', icon: Landmark }
            ].map((service, idx) => (
              <div 
                key={idx} 
                className="group bg-[#110a14] border border-[#800020]/20 hover:border-[#D4AF37] p-8 hover:-translate-y-2 transition-all cursor-pointer"
                onClick={() => setActiveModal(service.title)}
                data-testid={`service-card-${idx}`}
              >
                <service.icon className="h-14 w-14 text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
                <p className="text-[#D4AF37] text-sm font-bold mt-4 uppercase tracking-wider">Learn More →</p>
              </div>
            ))}
          </div>

          {/* Additional Practice Areas */}
          <div className="mt-16 bg-[#110a14] border border-[#800020]/20 p-10">
            <h3 className="text-3xl font-black text-white mb-8 text-center uppercase tracking-wider">Additional Practice Areas</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'Labor, Agency & Distribution',
                'Banking & Insurance',
                'Food, Beverage & Tobacco',
                'Health Care & Biotechnology',
                'Charity & Non-Profit',
                'Equity & Private Investments'
              ].map((area, idx) => (
                <div key={idx} className="flex items-center text-white font-semibold">
                  <ChevronRight className="h-5 w-5 text-[#D4AF37] mr-3" />
                  {area}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-32 px-6 md:px-12 bg-[#0a0008]" data-testid="team-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.3em]">Our Experts</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>Meet Our Team</h2>
            <p className="text-xl text-gray-300">Multilingual professionals with profound expertise</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Salvatore Di Salvatore', role: 'Founder & Visionary Leader', description: 'Qualified lawyer in UK and Italy' },
              { name: 'Piergiorgio Luongo', role: 'Head of Litigation', description: 'Excellent practitioner in contentious legal work' },
              { name: 'Viviana De Marco', role: 'Senior Counsel', description: 'Dedicated to making legal services accessible' },
              { name: 'Pierre-Louis Dauzier', role: 'Paris Partner', description: 'Trusted partner practicing law in Paris' },
              { name: 'Pier Luigi Congedo', role: 'Competition Law Expert', description: 'Academic expert of UK & EU Competition Law' },
              { name: 'Charlotte Winton', role: 'UK Partner', description: 'Partner at Winton & Winton Limited' },
              { name: 'Fabrizio Cofini', role: 'Head of Accounting', description: 'Expert in accounting and financial concepts' }
            ].map((member, idx) => (
              <div 
                key={idx} 
                className="group bg-[#110a14] border border-[#800020]/30 hover:border-[#D4AF37] p-8 transition-all hover:-translate-y-2"
                data-testid={`team-member-${idx}`}
              >
                <div className="w-28 h-28 bg-gradient-to-br from-[#800020] to-[#A91D3A] rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <span className="text-4xl font-black text-white">{member.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <h3 className="text-xl font-bold text-white text-center mb-2">{member.name}</h3>
                <p className="text-[#D4AF37] font-semibold text-center mb-3 text-sm uppercase tracking-wider">{member.role}</p>
                <p className="text-gray-400 text-center text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 md:px-12 bg-[#0d0510]" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-[#800020] to-[#D4AF37] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.3em]">Get In Touch</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>Contact Us</h2>
            <p className="text-xl text-gray-400">Ready to work with the best? Let's discuss your needs</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-[#110a14] border border-[#800020]/30 p-10">
              <h3 className="text-3xl font-black text-white mb-8 uppercase">Send Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-gray-300 font-bold mb-3 text-sm uppercase tracking-wider">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-black border border-[#800020]/30 focus:border-[#D4AF37] outline-none transition text-white"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-bold mb-3 text-sm uppercase tracking-wider">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-black border border-[#800020]/30 focus:border-[#D4AF37] outline-none transition text-white"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-bold mb-3 text-sm uppercase tracking-wider">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-black border border-[#800020]/30 focus:border-[#D4AF37] outline-none transition text-white"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-bold mb-3 text-sm uppercase tracking-wider">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-black border border-[#800020]/30 focus:border-[#D4AF37] outline-none transition resize-none text-white"
                    data-testid="contact-message-input"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitStatus === 'sending'}
                  className="w-full bg-[#800020] hover:bg-[#A91D3A] text-white px-8 py-5 font-black uppercase tracking-[0.2em] hover:scale-105 transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(128,0,32,0.4)]"
                  data-testid="contact-submit-button"
                >
                  {submitStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <p className="text-green-400 font-bold text-center" data-testid="success-message">✓ Message sent successfully!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 font-bold text-center" data-testid="error-message">✗ Failed to send. Please try again.</p>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-[#110a14] border border-[#800020]/30 p-10">
                <h3 className="text-3xl font-black text-white mb-8 uppercase">Our Offices</h3>
                <div className="space-y-8">
                  <div className="flex items-start">
                    <MapPin className="h-8 w-8 text-[#D4AF37] mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-white text-xl mb-1">London Office</p>
                      <p className="text-gray-400 text-lg">60 Cannon Street, London, EC4N 6NP, UK</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-8 w-8 text-[#D4AF37] mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-white text-xl mb-1">Milan Office</p>
                      <p className="text-gray-400 text-lg">38/2 Via Venini, 20127, Milan, Italy</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Phone className="h-8 w-8 text-[#D4AF37] mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-white text-xl mb-1">Phone</p>
                      <p className="text-gray-400">UK: +44 20 8058 9313</p>
                      <p className="text-gray-400">Italy: +39 02 2614 3865</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="h-8 w-8 text-[#D4AF37] mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-white text-xl mb-1">Email</p>
                      <p className="text-gray-400">info@accomplyglobal.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#800020] border-t-4 border-[#D4AF37] p-10">
                <h4 className="text-2xl font-black text-white mb-6 uppercase">Business Hours</h4>
                <div className="space-y-3 text-white">
                  <p><span className="text-[#D4AF37] font-bold">Monday - Friday:</span> 09:00 - 17:00</p>
                  <p><span className="text-[#D4AF37] font-bold">Saturday - Sunday:</span> Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-white border-t-4 border-[#800020] py-12 px-6 md:px-12 lg:px-16" data-testid="footer">
        <div className="w-full flex flex-col items-center text-center">
          <img 
            src="/accomply-logo.png" 
            alt="Accomply Global" 
            className="h-24 md:h-32 mb-6"
          />
          <p className="text-gray-700 mb-3 text-lg font-semibold">Elite Legal Services Worldwide</p>
          <p className="text-gray-500">© 2025 Accomply Global. All rights reserved.</p>
        </div>
      </footer>

      {/* Practice Area Detail Modal */}
      {activeModal && practiceDetails[activeModal] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="practice-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)}></div>
          <div className="relative bg-[#110a14] border border-[#800020]/40 max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="practice-modal-content">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#800020] to-[#A91D3A] p-8 flex items-center justify-between z-10">
              <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-wider">{activeModal}</h2>
              <button onClick={() => setActiveModal(null)} className="text-white hover:text-[#D4AF37] transition-colors" data-testid="modal-close-button">
                <X className="h-8 w-8" />
              </button>
            </div>

            <div className="p-8 md:p-12 space-y-10">
              {/* Overview */}
              <div>
                <h3 className="text-xl font-black text-[#D4AF37] uppercase tracking-wider mb-4">Overview</h3>
                <p className="text-gray-300 leading-relaxed text-lg">{practiceDetails[activeModal].overview}</p>
              </div>

              {/* Key Services */}
              <div>
                <h3 className="text-xl font-black text-[#D4AF37] uppercase tracking-wider mb-4">Key Services</h3>
                <ul className="space-y-3">
                  {practiceDetails[activeModal].keyServices.map((service, idx) => (
                    <li key={idx} className="flex items-start text-gray-300">
                      <ChevronRight className="h-5 w-5 text-[#D4AF37] mt-1 mr-3 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Insights */}
              <div>
                <h3 className="text-xl font-black text-[#D4AF37] uppercase tracking-wider mb-6">Legal Insights & News</h3>
                <div className="space-y-6">
                  {practiceDetails[activeModal].insights.map((insight, idx) => (
                    <div key={idx} className="bg-[#0a0008] border-l-4 border-[#800020] p-6">
                      <h4 className="text-white font-bold text-lg mb-2">{insight.title}</h4>
                      <p className="text-gray-400 leading-relaxed">{insight.summary}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-[#800020]/20 to-transparent border border-[#800020]/30 p-8 text-center">
                <p className="text-white text-lg mb-4">Need expert advice on <span className="text-[#D4AF37] font-bold">{activeModal}</span>?</p>
                <button 
                  onClick={() => { setActiveModal(null); setTimeout(() => scrollToSection('contact'), 300); }}
                  className="bg-[#800020] hover:bg-[#A91D3A] text-white px-10 py-4 font-black uppercase tracking-[0.2em] hover:scale-105 transition-all shadow-lg"
                  data-testid="modal-contact-cta"
                >
                  Contact Our Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;